import React, { useCallback, useEffect, useState } from "react";
import {
  Notice,
  BLANK_NOTICE,
  fetchNotices,
  saveNotice,
  deleteNotice,
  newNoticeId,
  isNoticeVisible,
  daysRemaining,
  imageToDataUri,
  endOfDayIso,
  isoToDateInput,
} from "../services/NoticeService";
import { adminSignIn, adminSignOut, watchAdmin } from "../services/firebase";
import type { User } from "firebase/auth";

const MUTED = "#8A9992";
const DIM = "#7A8880";
const SOFT = "#D6E2DC";
const MINT = "#5EE9B5";
const AMBER = "#F5B44C";
const RED = "#F0616B";
const INK = "#F2F7F4";

const LABEL: React.CSSProperties = {
  fontSize: 11,
  color: DIM,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  fontWeight: 600,
};

const INPUT: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  background: "rgba(0,0,0,0.4)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 8,
  padding: "9px 11px",
  color: INK,
  fontSize: 13,
  fontFamily: "'Sora',sans-serif",
  outline: "none",
};

const chip = (tone = MINT, disabled = false): React.CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  border: `1px solid ${tone}40`,
  background: `${tone}14`,
  color: tone,
  borderRadius: 999,
  padding: "4px 10px",
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: "'Sora',sans-serif",
  fontSize: 11,
  fontWeight: 600,
  opacity: disabled ? 0.5 : 1,
  whiteSpace: "nowrap",
});

const primaryBtn = (disabled: boolean): React.CSSProperties => ({
  border: 0,
  borderRadius: 9,
  padding: "10px 16px",
  fontSize: 13,
  fontWeight: 600,
  fontFamily: "'Sora',sans-serif",
  cursor: disabled ? "not-allowed" : "pointer",
  background: "linear-gradient(180deg, #19B37C, #0E8A5C)",
  color: "#03150D",
  opacity: disabled ? 0.5 : 1,
});

const TONE_DOT: Record<Notice["tone"], string> = {
  info: MINT,
  warning: AMBER,
  critical: RED,
};

/**
 * Manage site-wide notices. Centred modal, opened from the admin status strip.
 *
 * Writes require a signed-in Firebase admin — Firestore rules enforce this, so
 * an unauthenticated visitor cannot edit notices even with the client config.
 */
const NoticePanel: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [user, setUser] = useState<User | null>(null);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<Notice | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  useEffect(() => watchAdmin(setUser), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setNotices(await fetchNotices());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [open, onClose]);

  if (!open) return null;

  const set = <K extends keyof Notice>(k: K, v: Notice[K]) =>
    setDraft((d) => (d ? { ...d, [k]: v } : d));

  const handleSignIn = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await adminSignIn(email.trim(), password);
      setPassword("");
    } catch (error: any) {
      setMessage({ ok: false, text: error?.message?.replace("Firebase: ", "") || "Sign-in failed." });
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!draft.title.trim() && !draft.imageUrl) {
      setMessage({ ok: false, text: "Add a title or an image." });
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await saveNotice(draft);
      setMessage({ ok: true, text: "Notice saved. It is live for visitors now." });
      setDraft(null);
      await load();
    } catch (error: any) {
      console.error("[NoticePanel] save failed", error);
      setMessage({
        ok: false,
        text: error?.code === "permission-denied"
          ? "Permission denied — check the Firestore rules allow writes when signed in."
          : error?.message || "Could not save.",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (n: Notice) => {
    if (!window.confirm(`Delete "${n.title || "this notice"}"? This cannot be undone.`)) return;
    setBusy(true);
    setMessage(null);
    try {
      await deleteNotice(n.id);
      setMessage({ ok: true, text: "Notice deleted." });
      if (draft?.id === n.id) setDraft(null);
      await load();
    } catch (error: any) {
      setMessage({ ok: false, text: error?.message || "Could not delete." });
    } finally {
      setBusy(false);
    }
  };

  const handleImage = async (file: File) => {
    setBusy(true);
    setMessage(null);
    try {
      set("imageUrl", await imageToDataUri(file));
    } catch (error: any) {
      setMessage({ ok: false, text: error?.message || "Could not process image." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,2,5,0.72)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        zIndex: 60,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="cvt-scroll"
        style={{
          width: "min(680px,100%)",
          maxHeight: "86vh",
          overflowY: "auto",
          background: "#060B08",
          border: "1px solid rgba(255,255,255,0.10)",
          borderRadius: 16,
          padding: 22,
          display: "flex",
          flexDirection: "column",
          gap: 14,
          fontFamily: "'Sora',sans-serif",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: INK }}>Site notices</h2>
            <p style={{ margin: "5px 0 0", fontSize: 12, color: MUTED }}>
              Banners shown to every visitor. Auto-hide when they expire.
            </p>
          </div>
          <button onClick={onClose} style={chip(MINT)}>Close</button>
        </div>

        {/* ── Auth gate ───────────────────────────────────────── */}
        {!user ? (
          <div
            style={{
              border: "1px solid rgba(245,180,76,0.3)",
              background: "rgba(245,180,76,0.06)",
              borderRadius: 10,
              padding: 14,
              display: "flex",
              flexDirection: "column",
              gap: 9,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600, color: AMBER }}>Admin sign-in required</div>
            <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.5 }}>
              Editing notices writes to Firestore, which is restricted to an
              authenticated admin. Viewing below still works without signing in.
            </div>
            <input
              style={INPUT}
              placeholder="Email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              style={INPUT}
              type="password"
              placeholder="Password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSignIn()}
            />
            <button onClick={handleSignIn} disabled={busy || !email || !password} style={primaryBtn(busy || !email || !password)}>
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <span style={{ fontSize: 12, color: MUTED }}>
              Signed in as <span style={{ color: SOFT }}>{user.email}</span>
            </span>
            <button onClick={() => adminSignOut()} style={chip(MUTED)}>Sign out</button>
          </div>
        )}

        {message && (
          <div
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              color: message.ok ? MINT : RED,
              background: message.ok ? "rgba(94,233,181,0.08)" : "rgba(240,97,107,0.08)",
              border: `1px solid ${message.ok ? "rgba(94,233,181,0.25)" : "rgba(240,97,107,0.25)"}`,
              borderRadius: 8,
              padding: "8px 10px",
            }}
          >
            {message.text}
          </div>
        )}

        {/* ── Editor ──────────────────────────────────────────── */}
        {draft && (
          <div
            style={{
              border: "1px solid rgba(94,233,181,0.22)",
              background: "rgba(52,211,153,0.05)",
              borderRadius: 12,
              padding: 14,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            <div style={{ ...LABEL, color: MINT }}>
              {notices.some((n) => n.id === draft.id) ? "Edit notice" : "New notice"}
            </div>

            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => set("active", e.target.checked)}
                style={{ accentColor: MINT, width: 15, height: 15 }}
              />
              <span style={{ fontSize: 13, color: SOFT }}>Active</span>
            </label>

            <div>
              <div style={{ ...LABEL, marginBottom: 5 }}>Title</div>
              <input style={INPUT} value={draft.title} onChange={(e) => set("title", e.target.value)} />
            </div>

            <div>
              <div style={{ ...LABEL, marginBottom: 5 }}>Message</div>
              <textarea
                style={{ ...INPUT, minHeight: 70, resize: "vertical" }}
                value={draft.body}
                onChange={(e) => set("body", e.target.value)}
              />
            </div>

            {/* Banner vs popup. A poster-style image is unreadable squeezed
                into a top strip, so it gets a centred modal instead. */}
            <div>
              <div style={{ ...LABEL, marginBottom: 6 }}>Show as</div>
              <div style={{ display: "flex", gap: 6 }}>
                {([
                  { v: "banner", label: "Banner", hint: "Strip at the top of the page" },
                  { v: "popup", label: "Popup", hint: "Centred over the page" },
                ] as const).map((o) => {
                  const on = (draft.display ?? "banner") === o.v;
                  return (
                    <button
                      key={o.v}
                      onClick={() => set("display", o.v)}
                      title={o.hint}
                      style={{
                        flex: 1,
                        padding: "9px 10px",
                        borderRadius: 9,
                        border: `1px solid ${on ? `${MINT}66` : "rgba(255,255,255,0.12)"}`,
                        background: on ? `${MINT}18` : "rgba(0,0,0,0.3)",
                        color: on ? MINT : MUTED,
                        cursor: "pointer",
                        fontFamily: "'Sora',sans-serif",
                        fontSize: 12,
                        fontWeight: 600,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      {o.label}
                      <span style={{ fontSize: 10, fontWeight: 400, color: DIM }}>{o.hint}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tone as a segmented control — three options do not warrant a
                dropdown, and the colour swatch makes the choice self-evident. */}
            <div>
              <div style={{ ...LABEL, marginBottom: 6 }}>Tone</div>
              <div style={{ display: "flex", gap: 6 }}>
                {(["info", "warning", "critical"] as const).map((t) => {
                  const on = draft.tone === t;
                  return (
                    <button
                      key={t}
                      onClick={() => set("tone", t)}
                      style={{
                        flex: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 7,
                        padding: "8px 10px",
                        borderRadius: 9,
                        border: `1px solid ${on ? `${TONE_DOT[t]}66` : "rgba(255,255,255,0.12)"}`,
                        background: on ? `${TONE_DOT[t]}18` : "rgba(0,0,0,0.3)",
                        color: on ? TONE_DOT[t] : MUTED,
                        cursor: "pointer",
                        fontFamily: "'Sora',sans-serif",
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: "capitalize",
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 999,
                          background: TONE_DOT[t],
                          opacity: on ? 1 : 0.45,
                        }}
                      />
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Auto-hide: quick presets plus an explicit calendar date. */}
            <div>
              <div style={{ ...LABEL, marginBottom: 6 }}>Auto-hide</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[1, 3, 7, 14, 30].map((d) => {
                  const on = !draft.expiresAt && draft.expiresInDays === d;
                  return (
                    <button
                      key={d}
                      onClick={() => setDraft((x) => (x ? { ...x, expiresInDays: d, expiresAt: undefined } : x))}
                      style={{
                        padding: "7px 12px",
                        borderRadius: 999,
                        border: `1px solid ${on ? `${MINT}66` : "rgba(255,255,255,0.12)"}`,
                        background: on ? `${MINT}18` : "rgba(0,0,0,0.3)",
                        color: on ? MINT : MUTED,
                        cursor: "pointer",
                        fontFamily: "'Sora',sans-serif",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {d}d
                    </button>
                  );
                })}
                <button
                  onClick={() =>
                    setDraft((x) =>
                      x
                        ? {
                            ...x,
                            expiresAt:
                              x.expiresAt ??
                              endOfDayIso(isoToDateInput(new Date(Date.now() + 7 * 86400000).toISOString())),
                          }
                        : x
                    )
                  }
                  style={{
                    padding: "7px 12px",
                    borderRadius: 999,
                    border: `1px solid ${draft.expiresAt ? `${MINT}66` : "rgba(255,255,255,0.12)"}`,
                    background: draft.expiresAt ? `${MINT}18` : "rgba(0,0,0,0.3)",
                    color: draft.expiresAt ? MINT : MUTED,
                    cursor: "pointer",
                    fontFamily: "'Sora',sans-serif",
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Pick a date
                </button>
              </div>

              {draft.expiresAt && (
                <div style={{ marginTop: 8 }}>
                  <input
                    type="date"
                    style={{ ...INPUT, colorScheme: "dark" }}
                    min={isoToDateInput(new Date().toISOString())}
                    value={isoToDateInput(draft.expiresAt)}
                    onChange={(e) =>
                      e.target.value && set("expiresAt", endOfDayIso(e.target.value))
                    }
                  />
                  <div style={{ fontSize: 11, color: DIM, marginTop: 5 }}>
                    Hides at 23:59:59 on the selected day, your local time.
                  </div>
                </div>
              )}
            </div>

            <div>
              <div style={{ ...LABEL, marginBottom: 5 }}>Image (optional)</div>
              {draft.imageUrl ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <img
                    src={draft.imageUrl}
                    alt="Notice"
                    style={{ maxHeight: 48, borderRadius: 6, border: "1px solid rgba(255,255,255,0.12)" }}
                  />
                  <button onClick={() => set("imageUrl", undefined)} style={chip(MUTED)}>Remove</button>
                </div>
              ) : (
                <input
                  type="file"
                  accept="image/*"
                  disabled={busy}
                  onChange={(e) => e.target.files?.[0] && handleImage(e.target.files[0])}
                  style={{ ...INPUT, padding: 8, fontSize: 12, colorScheme: "dark" }}
                />
              )}
              <div style={{ fontSize: 11, color: DIM, marginTop: 5 }}>
                Shown below the message. Resized and compressed in the browser.
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setDraft(null)} style={chip(MUTED)}>Cancel</button>
              <button onClick={handleSave} disabled={busy || !user} style={primaryBtn(busy || !user)}>
                {busy ? "Saving…" : "Save notice"}
              </button>
            </div>
          </div>
        )}

        {/* ── List ────────────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <div style={{ ...LABEL }}>
            {loading ? "Loading…" : `${notices.length} notice${notices.length === 1 ? "" : "s"}`}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={load} disabled={loading} style={chip(MINT, loading)}>Refresh</button>
            <button
              onClick={() => setDraft({ ...BLANK_NOTICE, id: newNoticeId(), publishedAt: new Date().toISOString() })}
              disabled={!user}
              style={chip(MINT, !user)}
            >
              + New
            </button>
          </div>
        </div>

        {!loading && !notices.length && (
          <div style={{ fontSize: 12, color: MUTED }}>
            No notices yet. Create one to show a banner across the site.
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {notices.map((n) => {
            const live = isNoticeVisible(n);
            return (
              <div
                key={n.id}
                style={{
                  border: "1px solid rgba(255,255,255,0.09)",
                  background: "rgba(255,255,255,0.02)",
                  borderRadius: 10,
                  padding: 12,
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  opacity: live ? 1 : 0.6,
                }}
              >
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 999,
                    background: live ? TONE_DOT[n.tone] : MUTED,
                    marginTop: 6,
                    flex: "none",
                  }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: SOFT, overflowWrap: "anywhere" }}>
                    {n.title || "(image only)"}
                  </div>
                  {n.imageUrl && (
                    <img
                      src={n.imageUrl}
                      alt=""
                      style={{ maxHeight: 40, borderRadius: 5, marginTop: 5, display: "block" }}
                    />
                  )}
                  <div style={{ fontSize: 11, color: DIM, marginTop: 4 }}>
                    {live ? `Live · ${daysRemaining(n)}d left` : n.active ? "Expired" : "Inactive"}
                    {" · "}
                    {n.display === "popup" ? "popup" : "banner"}
                    {" · "}
                    {n.tone}
                    {" · "}
                    {new Date(n.publishedAt).toLocaleDateString()}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flex: "none" }}>
                  <button onClick={() => setDraft(n)} disabled={!user} style={chip(MINT, !user)}>Edit</button>
                  <button onClick={() => handleDelete(n)} disabled={!user || busy} style={chip(RED, !user || busy)}>
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NoticePanel;
